import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { useEventListener } from 'usehooks-ts';
import { LocalStorageService, RootState } from '../configuration';
import { useConnectionBuilder } from '../hooks/use-connection/useConnection';
import { HubEvent, HubName } from '../enums/hubnames';
import toast from 'react-hot-toast';
import { IChat, IChatDating } from '../types/chat';
import { useDating } from '../hooks/use-dating/useDating';

interface ISignalRContext {
  state: {
    connectionChat: HubConnection | null;
    setConnection: Dispatch<SetStateAction<HubConnection | null>>;
    connectionNoti: HubConnection | null;
    connectionDating: HubConnection | null;
  };
}

export const SignalRContext = createContext<ISignalRContext>({
  state: {
    connectionChat: null,
    setConnection: () => null,
    connectionNoti: null,
    connectionDating: null,
  },
});

const SignalRProvider = ({ children }: PropsWithChildren) => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  const userProfile = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  const { datingProfile } = useDating({});

  const { connection: connectionChat, setConnection } = useConnectionBuilder(
    HubName.Chat
  );
  const { connection: connectionPresent } = useConnectionBuilder(
    HubName.Present
  );
  const { connection: connectionNoti } = useConnectionBuilder(HubName.Noti);
  const { connection: connectionDating } = useConnectionBuilder(HubName.Dating);

  // const createSignalHandler = (hubName: HubName) => {
  //   return () => {
  //     if (connection?.state === HubConnectionState.Connected) {
  //       connection.on(hubName, (signal: any) => {
  //         if (signal) {
  //           console.log('signal', signal);
  //         }
  //       });
  //     }
  //   };
  // };

  const invokeHandler = () => {
    const accessToken = LocalStorageService.getItem('accessToken');
  };
  const setupConnectionNoti = () => {
    if (
      connectionNoti &&
      connectionNoti.state === HubConnectionState.Disconnected
    ) {
      connectionNoti
        .start()
        .then(() => {
          invokeHandler();
        })
        .then(() => {
          //
        })
        .catch((error) => {
          console.log(
            `error occurred while starting connection: ${error.message}`
          );
        });

      connectionNoti.onclose((error) => {
        //
      });

      connectionNoti.onreconnected(() => {
        if (connectionPresent?.state === HubConnectionState.Connected) {
          invokeHandler();
        }
      });
    }
  };
  const setupConnection = () => {
    if (
      connectionChat &&
      connectionChat.state === HubConnectionState.Disconnected
    ) {
      connectionChat
        .start()
        .then(() => {
          invokeHandler();
        })
        .then(() => {
          //
        })
        .catch((error) => {
          console.log(
            `error occurred while starting connection: ${error.message}`
          );
        });

      connectionChat.onclose((error) => {
        //
      });

      connectionChat.onreconnected(() => {
        if (connectionChat?.state === HubConnectionState.Connected) {
          invokeHandler();
        }
      });
    }
  };

  const setupConnectionPresent = () => {
    if (
      connectionPresent &&
      connectionPresent.state === HubConnectionState.Disconnected
    ) {
      connectionPresent
        .start()
        .then(() => {
          invokeHandler();
        })
        .then(() => {
          //
        })
        .catch((error) => {
          console.log(
            `error occurred while starting connection: ${error.message}`
          );
        });

      connectionPresent.onclose((error) => {
        //
      });

      connectionPresent.onreconnected(() => {
        if (connectionPresent?.state === HubConnectionState.Connected) {
          invokeHandler();
        }
      });
    }
  };

  const setupConnectionDating = () => {
    if (
      connectionDating &&
      connectionDating.state === HubConnectionState.Disconnected
    ) {
      connectionDating
        .start()
        .then(() => {
          invokeHandler();
        })
        .then(() => {
          //
        })
        .catch((error) => {
          console.log(
            `error occurred while starting connection: ${error.message}`
          );
        });

      connectionDating.onclose((error) => {
        //
      });

      connectionDating.onreconnected(() => {
        if (connectionDating?.state === HubConnectionState.Connected) {
          invokeHandler();
        }
      });
    }
  };

  useEventListener('online', () => {
    if (connectionChat?.state === HubConnectionState.Disconnected) {
      connectionChat.stop().then(() => {
        setupConnection();
      });
    }
  });

  useEventListener('online', () => {
    if (connectionDating?.state === HubConnectionState.Disconnected) {
      connectionDating.stop().then(() => {
        setupConnection();
      });
    }
  });

  useEffect(() => {
    if (!isAuthenticated || !connectionChat) {
      return;
    }

    setupConnectionNoti();
    // check if  includes /admin === false
    if (window.location.pathname.includes('/admin') === false) {
      setupConnection();
      setupConnectionPresent();
      setupConnectionDating();
    } else {
      connectionChat?.stop();
    }

    return () => {
      if (connectionChat?.state === HubConnectionState.Connected) {
        connectionChat.stop().then(() => {
          console.log('stop connection.');
        });
      }

      if (connectionPresent?.state === HubConnectionState.Connected) {
        connectionPresent.stop().then(() => {
          console.log('stop connectionPresent.');
        });
      }

      if (connectionNoti?.state === HubConnectionState.Connected) {
        connectionNoti.stop().then(() => {
          console.log('stop connectionNoti.');
        });
      }

      if (connectionDating?.state === HubConnectionState.Connected) {
        connectionDating.stop().then(() => {
          console.log('stop connectionNoti.');
        });
      }
    };
  }, [isAuthenticated, connectionChat, connectionNoti, connectionDating]);

  useEffect(() => {
    if (!connectionChat || !userProfile) {
      return;
    }

    const onChat = (message: IChat) => {
      const isNewMessage = message.recipientUserId === userProfile.userId;
      isNewMessage &&
        toast.success(`Bạn có tin nhắn mới từ ${message.senderName}`, {
          duration: 2000,
        });
    };
    const onDating = (message: IChatDating) => {
      const isNewMessage = message.recipientUserId === datingProfile?.profileId;
      isNewMessage &&
        toast.success(`Bạn có tin nhắn mới từ ${message.senderName}`, {
          duration: 2000,
        });
    };
    const onNoti = () => {
      toast.success('Bạn có thông báo mới', {
        duration: 2000,
      });
    };

    connectionChat?.on(HubEvent.NewMessage, onChat);
    connectionDating?.on(HubEvent.NewMessageDating, onDating);
    connectionNoti?.on(HubEvent.NewNotification, onNoti);

    // Gỡ đúng handler của effect này khi chạy lại, tránh đăng ký chồng → 1 tin bật nhiều toast.
    // Chỉ gỡ theo tham chiếu handler nên không đụng listener của trang thông báo/header.
    return () => {
      connectionChat?.off(HubEvent.NewMessage, onChat);
      connectionDating?.off(HubEvent.NewMessageDating, onDating);
      connectionNoti?.off(HubEvent.NewNotification, onNoti);
    };
  }, [connectionChat, userProfile, connectionDating, connectionNoti, datingProfile]);

  return (
    <SignalRContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        state: {
          connectionChat,
          setConnection,
          connectionNoti,
          connectionDating,
        },
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
};

export default SignalRProvider;
